/* eslint-disable react/prop-types */
import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * EntityShell — Phase 6 foundation component.
 *
 * The shared shell every detail page plugs into so the product LOOKS like
 * one app.  Trust Contract compliant by design:
 *   • NO modals (Rule 2).  Tabs, slide-overs, routed children only.
 *   • Mobile-first (Rule 5).  ≥44px tap targets, ≤390px viewport default.
 *   • Dates via capabilities/dates (Rule 3) — the footer is rendered by
 *     the caller passing a pre-formatted dd/mm/yyyy string.
 *
 * Props
 * -----
 *  shortId        : "JC-2026-0041"
 *  kindLabel      : "Job Card" | "Customer" | "Project" | ...
 *  title          : "Borehole rebuild — Sabie River Camp"
 *  status         : { label: "In Progress", tone: "amber" } (optional)
 *  customerLink   : { label, to } (optional)
 *  backTo         : navigation fallback for the back arrow (e.g. "/jobcards")
 *  actions        : ReactNode — primary actions row (right of header)
 *  tabs           : [{ key, label, count?, hidden? }, ...]
 *  activeTab      : key of currently active tab (controlled mode)
 *  onTabChange    : fn(key)
 *  defaultTab     : key — initial tab if uncontrolled
 *  footerMeta     : { createdByName, createdAtDisplay, updatedByName, updatedAtDisplay }
 *  children       : the body — typically the rendered content for `activeTab`
 *
 * Usage
 * -----
 *   const [tab, setTab] = useState("overview");
 *   return (
 *     <EntityShell
 *       shortId="JC-2026-0041" kindLabel="Job Card"
 *       title="Borehole rebuild — Sabie River Camp"
 *       status={{ label: "In Progress", tone: "amber" }}
 *       backTo="/jobcards"
 *       tabs={[
 *         { key: "overview", label: "Overview" },
 *         { key: "activity", label: "Activity", count: 12 },
 *         { key: "photos", label: "Photos", count: 4 },
 *       ]}
 *       activeTab={tab} onTabChange={setTab}
 *       actions={<button data-testid="entityshell-edit-btn">Edit</button>}
 *       footerMeta={{ createdByName: "Dirk", createdAtDisplay: "12/05/2026" }}
 *     >
 *       {tab === "overview" && <OverviewBlock />}
 *       {tab === "activity" && <HistoryBlock entity={jc} />}
 *     </EntityShell>
 *   );
 */

const TONE_BG = {
  slate: "border-slate-400/40 text-slate-200 bg-slate-500/10",
  blue: "border-blue-500/40 text-blue-300 bg-blue-500/10",
  amber: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  green: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  red: "border-red-500/40 text-red-300 bg-red-500/10",
  violet: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  cyan: "border-cyan-500/40 text-cyan-300 bg-cyan-500/10",
};

function StatusPill({ status }) {
  if (!status) return null;
  const cls = TONE_BG[status.tone] || TONE_BG.slate;
  return (
    <span
      data-testid="entityshell-status-pill"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        cls,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {status.label}
    </span>
  );
}

export default function EntityShell({
  shortId,
  kindLabel,
  title,
  status,
  customerLink,
  backTo,
  actions,
  tabs = [],
  activeTab,
  onTabChange,
  defaultTab,
  footerMeta,
  children,
}) {
  const navigate = useNavigate();
  const [internalTab, setInternalTab] = useState(
    defaultTab || (tabs.find((t) => !t.hidden) || {}).key,
  );
  const visibleTabs = useMemo(() => tabs.filter((t) => !t.hidden), [tabs]);
  const tab = activeTab ?? internalTab;
  const setTab = onTabChange || setInternalTab;

  return (
    <div
      data-testid="entity-shell"
      className="min-h-screen bg-appbg text-slate-100"
    >
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <header
        data-testid="entity-shell-header"
        className="sticky top-0 z-30 border-b border-appline bg-appbg/85 backdrop-blur"
      >
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6">
          <button
            data-testid="entity-shell-back-btn"
            onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-300 hover:bg-slate-800/60 hover:text-slate-100 active:bg-slate-800/80"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {kindLabel && (
                <span
                  data-testid="entity-shell-kind"
                  className="text-xs uppercase tracking-wider text-slate-400"
                >
                  {kindLabel}
                </span>
              )}
              {shortId && (
                <span
                  data-testid="entity-shell-shortid"
                  className="rounded bg-slate-800/70 px-1.5 py-0.5 font-mono text-xs text-slate-300"
                >
                  {shortId}
                </span>
              )}
              <StatusPill status={status} />
            </div>
            <h1
              data-testid="entity-shell-title"
              className="mt-0.5 truncate text-lg font-semibold text-slate-50 sm:text-xl"
            >
              {title || "—"}
            </h1>
            {customerLink && (
              <Link
                to={customerLink.to}
                data-testid="entity-shell-customer-link"
                className="mt-0.5 block truncate text-sm text-cyan-300/90 hover:text-cyan-200"
              >
                {customerLink.label}
              </Link>
            )}
          </div>

          {actions && (
            <div
              data-testid="entity-shell-actions"
              className="flex shrink-0 items-center gap-2"
            >
              {actions}
            </div>
          )}
        </div>

        {/* Tab strip */}
        {visibleTabs.length > 0 && (
          <div
            data-testid="entity-shell-tab-strip"
            className="mx-auto flex max-w-6xl gap-1 overflow-x-auto border-t border-slate-800/60 px-2 sm:px-4"
          >
            {visibleTabs.map((t) => {
              const isActive = t.key === tab;
              return (
                <button
                  key={t.key}
                  data-testid={`entity-shell-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "flex h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm transition-colors",
                    isActive
                      ? "border-cyan-400 text-cyan-200"
                      : "border-transparent text-slate-400 hover:text-slate-200",
                  )}
                >
                  {t.label}
                  {typeof t.count === "number" && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 text-[10px] font-medium",
                        isActive
                          ? "bg-cyan-400/15 text-cyan-200"
                          : "bg-slate-800 text-slate-400",
                      )}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <main
        data-testid="entity-shell-body"
        className="mx-auto max-w-6xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6"
      >
        {children}
      </main>

      {/* ── Audit footer ──────────────────────────────────────────── */}
      {footerMeta && (
        <footer
          data-testid="entity-shell-footer"
          className="mx-auto max-w-6xl border-t border-slate-800 px-4 py-3 text-xs text-slate-500 sm:px-6"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {footerMeta.createdByName && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Created by {footerMeta.createdByName}
                {footerMeta.createdAtDisplay && (
                  <> on {footerMeta.createdAtDisplay}</>
                )}
              </span>
            )}
            {footerMeta.updatedByName && (
              <span>
                · Last update by {footerMeta.updatedByName}
                {footerMeta.updatedAtDisplay && (
                  <> on {footerMeta.updatedAtDisplay}</>
                )}
              </span>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
